import { useRef, useState } from 'react';
import { Reorder } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { TextSmMd, TextSmRg } from '@/UI/AppTypography/AppTypography';
import { I18N_KEYS } from '@/language/keys';
import { Flex, FlexCenVer, FlexCol } from '@/UI/styles';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { COMMUNITY_PUBLISH_IMAGE_TYPES } from '@/utils/fileTypes';
import { useHorizontalWheelScroll } from '@/hooks/useHorizontalWheelScroll';
import PublishImageDropzone, { type PublishImageDropzoneHandle } from './PublishImageDropzone';
import type { PublishImage } from '@/state/publishToCommunityForm.state';

export const CAROUSEL_MAX = 9;
const CAROUSEL_MIN_VISIBLE = 3;
const ACCEPTED_IMAGE_INPUT_ATTR = Object.keys(COMMUNITY_PUBLISH_IMAGE_TYPES).join(',');

interface PublishToCommunityModalImagesProps {
  isPublishingAsTool?: boolean;
  isOrgPublish?: boolean;
  thumbnailPreviewUrl: string;
  onThumbnailFile: (file: File) => void;
  onThumbnailRemove: () => void;
  iconPreviewUrl?: string;
  iconRequired?: boolean;
  onIconFile?: (file: File) => void;
  onIconRemove?: () => void;
  imageError: string | null;
  iconError?: string | null;
  carouselImages: PublishImage[];
  carouselPreviewUrls: string[];
  onCarouselFile: (index: number, file: File) => void;
  onCarouselRemove: (index: number) => void;
  onCarouselReorder: (images: PublishImage[]) => void;
}

const PublishToCommunityModalImages = ({
  isPublishingAsTool = false,
  isOrgPublish = false,
  thumbnailPreviewUrl,
  onThumbnailFile,
  onThumbnailRemove,
  iconPreviewUrl = '',
  iconRequired = true,
  onIconFile,
  onIconRemove,
  imageError,
  iconError = null,
  carouselImages,
  carouselPreviewUrls,
  onCarouselFile,
  onCarouselRemove,
  onCarouselReorder,
}: PublishToCommunityModalImagesProps) => {
  const { t } = useTranslation();
  const showError = Boolean(imageError) && !thumbnailPreviewUrl;
  const showIconError = Boolean(iconError) && !iconPreviewUrl;

  const thumbnailDropzoneRef = useRef<PublishImageDropzoneHandle>(null);
  const iconDropzoneRef = useRef<PublishImageDropzoneHandle>(null);
  const carouselMultiInputRef = useRef<HTMLInputElement>(null);
  const carouselRef = useHorizontalWheelScroll();

  const [isReordering, setIsReordering] = useState(false);

  const filledCount = carouselImages.length;
  const carouselFull = filledCount >= CAROUSEL_MAX;

  // Show CAROUSEL_MIN_VISIBLE slots minimum; once filled past that, keep one trailing empty slot.
  const trailingEmptyCount = carouselFull ? 0 : Math.max(1, CAROUSEL_MIN_VISIBLE - filledCount);

  return (
    <>
      {isPublishingAsTool && onIconFile && (
        <FlexCol sx={{ gap: 3 }}>
          <FlexCenVer sx={{ justifyContent: 'space-between', gap: 2 }}>
            <FlexCol>
              <TextSmMd>
                {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.IMAGES_STEP.ICON_LABEL)}
                {iconRequired && (
                  <span aria-hidden style={showIconError ? { color: token.textDanger } : undefined}>
                    {' *'}
                  </span>
                )}
              </TextSmMd>
              <TextSmRg color={token.textSecondary}>
                {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.IMAGES_STEP.ICON_RECOMMENDED_SIZE)}
              </TextSmRg>
            </FlexCol>
            <ButtonContained
              mode="filled-secondary"
              size="xs"
              onClick={() => iconDropzoneRef.current?.open()}
              sx={{ width: 56, minWidth: 56 }}
            >
              {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.IMAGES_STEP.UPLOAD)}
            </ButtonContained>
          </FlexCenVer>
          <FlexCol sx={{ gap: '4px' }}>
            <PublishImageDropzone
              ref={iconDropzoneRef}
              width={90}
              height={90}
              previewUrl={iconPreviewUrl}
              onFile={onIconFile}
              onRemove={onIconRemove}
              hasError={Boolean(iconError)}
            />
            {showIconError && (
              <TextSmRg color={token.textDanger} sx={{ px: '4px' }}>
                {iconError}
              </TextSmRg>
            )}
          </FlexCol>
        </FlexCol>
      )}

      <FlexCol sx={{ gap: 3 }}>
        <FlexCenVer sx={{ justifyContent: 'space-between', gap: 2 }}>
          <FlexCol>
            <TextSmMd>
              {t(
                isPublishingAsTool
                  ? I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.IMAGES_STEP.THUMBNAIL_LABEL_TOOL
                  : I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.IMAGES_STEP.THUMBNAIL_LABEL,
              )}
              <span aria-hidden style={showError ? { color: token.textDanger } : undefined}>
                {' *'}
              </span>
            </TextSmMd>
            <TextSmRg color={token.textSecondary}>
              {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.IMAGES_STEP.RECOMMENDED_SIZE)}
            </TextSmRg>
          </FlexCol>
          <ButtonContained
            mode="filled-secondary"
            size="xs"
            onClick={() => thumbnailDropzoneRef.current?.open()}
            sx={{ width: 56, minWidth: 56 }}
          >
            {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.IMAGES_STEP.UPLOAD)}
          </ButtonContained>
        </FlexCenVer>
        <FlexCol sx={{ gap: '4px' }}>
          <PublishImageDropzone
            ref={thumbnailDropzoneRef}
            width="100%"
            height={216}
            previewUrl={thumbnailPreviewUrl}
            onFile={onThumbnailFile}
            onRemove={onThumbnailRemove}
            hasError={Boolean(imageError)}
          />
          {showError && (
            <TextSmRg color={token.textDanger} sx={{ px: '4px' }}>
              {imageError}
            </TextSmRg>
          )}
        </FlexCol>
      </FlexCol>

      {!isOrgPublish && (
        <FlexCol sx={{ gap: 3 }}>
          <FlexCenVer sx={{ justifyContent: 'space-between', gap: 2 }}>
            <FlexCol>
              <TextSmMd>
                {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.IMAGES_STEP.GALLERY_LABEL)}
              </TextSmMd>
              <TextSmRg color={token.textSecondary}>
                {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.IMAGES_STEP.RECOMMENDED_SIZE)}
              </TextSmRg>
            </FlexCol>
            <ButtonContained
              mode="filled-secondary"
              size="xs"
              disabled={carouselFull}
              onClick={() => carouselMultiInputRef.current?.click()}
              sx={{ width: 56, minWidth: 56 }}
            >
              {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.IMAGES_STEP.UPLOAD)}
            </ButtonContained>
            <input
              ref={carouselMultiInputRef}
              type="file"
              accept={ACCEPTED_IMAGE_INPUT_ATTR}
              multiple
              hidden
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                const remaining = CAROUSEL_MAX - filledCount;
                files.slice(0, remaining).forEach((file, i) => {
                  onCarouselFile(filledCount + i, file);
                });
                e.target.value = '';
              }}
            />
          </FlexCenVer>
          <Flex
            ref={carouselRef}
            sx={{
              gap: 1,
              overflowX: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {filledCount > 0 && (
              <Reorder.Group
                as="div"
                axis="x"
                values={carouselImages}
                onReorder={onCarouselReorder}
                style={{ display: 'flex', gap: 8, padding: 0, margin: 0, listStyle: 'none' }}
              >
                {carouselImages.map((item, i) => (
                  <Reorder.Item
                    as="div"
                    key={item.id}
                    value={item}
                    style={{ display: 'flex', cursor: isReordering ? 'grabbing' : 'grab' }}
                    onDragStart={() => setIsReordering(true)}
                    onDragEnd={() => setIsReordering(false)}
                  >
                    <PublishImageDropzone
                      width={160}
                      height={90}
                      previewUrl={carouselPreviewUrls[i] ?? ''}
                      onFile={(f) => onCarouselFile(i, f)}
                      onRemove={() => onCarouselRemove(i)}
                      suppressHoverOverlay={isReordering}
                    />
                  </Reorder.Item>
                ))}
              </Reorder.Group>
            )}
            {Array.from({ length: trailingEmptyCount }, (_, i) => (
              <PublishImageDropzone
                key={`placeholder-${i}`}
                width={160}
                height={90}
                previewUrl=""
                // Picking on any empty slot appends to the next available position.
                onFile={(file) => onCarouselFile(filledCount, file)}
              />
            ))}
          </Flex>
        </FlexCol>
      )}
    </>
  );
};

export default PublishToCommunityModalImages;
